import { ref } from 'vue';
import { getRequest } from '@/System/Store/endpoints';
import { issuanceLabel } from '@enums/CompetenceType';

// Shared per-page cache so hover-scanning a list doesn't refetch the same
// breakdown. Only successful responses are cached — a transient error must
// be able to recover on the next hover/expand.
const breakdownCache = new Map();

// Certifikat påverkar utvärderingarna — anropas när certifikat skapas,
// raderas eller återställs så att listorna inte visar inaktuell status
// tills sidan laddas om.
export const clearExpertiseBreakdownCache = () => breakdownCache.clear();

/**
 * Fetches and formats the per-expertise status breakdown
 * (v2.employ.certificates.expertises.status-breakdown) for the hover popup
 * and the renewal panel.
 */
const useExpertiseBreakdown = (props) => {
    const loading = ref(false);
    const breakdown = ref(null);

    const cacheKey = () => `${props.expertiseId}:${props.userProfileId}`;

    const fetchBreakdown = () => {
        if (breakdown.value) {
            return;
        }
        if (breakdownCache.has(cacheKey())) {
            breakdown.value = breakdownCache.get(cacheKey());
            return;
        }

        loading.value = true;
        getRequest(
            route('v2.employ.certificates.expertises.status-breakdown', {
                expertise: props.expertiseId,
                userProfile: props.userProfileId,
            })
        )
            .then((response) => {
                if (response?.status === 'Success' && response.data) {
                    breakdown.value = response.data;
                    breakdownCache.set(cacheKey(), response.data);
                } else {
                    breakdown.value = null;
                }
            })
            .catch(() => (breakdown.value = null))
            .finally(() => (loading.value = false));
    };

    const competenceDetail = (competence) => {
        if (competence.reasons?.length) {
            return competence.reasons[0];
        }
        if (competence.certificate?.expires_at) {
            return `Giltig till ${competence.certificate.expires_at}`;
        }
        if (competence.certificate) {
            return 'Tills vidare';
        }
        return competence.is_main ? '' : 'Saknas';
    };

    const requirementLabel = (requirement) =>
        requirement.name || requirement.alternatives.map((alternative) => alternative.name).join(' eller ');

    // Namnlösa fortbildningskrav visas med de valda kompetensernas
    // (+ underområdenas) namn i stället för generiska "Fortbildning"
    const subRequirementLabel = (sub) =>
        sub.name || (sub.alternatives || []).map((alternative) => alternative.name).join(' eller ') || 'Fortbildning';

    const requirementDetail = (requirement, includeSubReasons = false) => {
        if (requirement.reasons?.length) {
            return requirement.reasons[0];
        }

        if (includeSubReasons) {
            const dueSubRequirement = requirement.sub_requirements?.find((sub) => sub.reasons?.length);
            if (dueSubRequirement) {
                return dueSubRequirement.reasons[0];
            }
        }

        if (requirement.satisfied_by?.expires_at) {
            return `Giltig till ${requirement.satisfied_by.expires_at}`;
        }

        return requirement.satisfied_by ? 'Tills vidare' : 'Saknas';
    };

    const subDetail = (sub) => {
        if (sub.reasons?.length) {
            return sub.reasons[0];
        }
        if (sub.mode === 'rolling_period' && sub.required_count !== null) {
            return `${sub.actual_count} av ${sub.required_count} repetitioner`;
        }
        if (sub.satisfied_by?.length) {
            const latest = sub.satisfied_by
                .filter((certificate) => certificate.issued_at)
                .sort((a, b) => a.issued_at.localeCompare(b.issued_at))
                .pop();
            const label = issuanceLabel(latest?.competence_type ?? sub.satisfied_by[0].competence_type);
            return latest ? `${label} ${latest.issued_at}` : label;
        }
        return sub.deadline ? `Ej genomförd — krävs för förnyelse, senast ${sub.deadline}` : '';
    };

    return {
        loading,
        breakdown,
        fetchBreakdown,
        competenceDetail,
        requirementLabel,
        subRequirementLabel,
        requirementDetail,
        subDetail,
    };
};

export default useExpertiseBreakdown;
